import { flexRender } from "@tanstack/react-table";
import { forwardRef, Fragment, useMemo } from "react";
import { useAtomValue } from "jotai";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";

import { IsMobileAtom } from "../../App";
import SkeletonLoader from "../../elements/SkeletonLoader";

interface Props {
  items?: any[];
  table: any;
  loading: boolean;
  error: any;
  selectable?: boolean;
  deps?: any[];
}

const selectedCSS = "bg-primary-selected";
const empty: [] = [];

/**
 * AppTableBody - The table body (tbody) for AppTable.
 * @param {Table} table -The React Table, Table object.
 * @param {boolean} loading - Whether or not AppTable is querying.
 */
const AppTableBody = ({
  items = [],
  table,
  loading,
  error,
  selectable = false,
  deps = empty,
}: Props) => {
  const isMobile = useAtomValue(IsMobileAtom);
  const rows = table?.getRowModel().rows;
  const columns = table?.getAllColumns();

  const cells = useMemo(() => {
    return rows.map((row: any) => {
      return row.getVisibleCells().map((cell: any) => {
        if (selectable && cell?.column?.id === "selectable-rows") return <></>;

        return ["selectable-rows", "_RecordName"].includes(cell?.column?.id) ? (
          <Fragment key={cell.id}>
            <div
              className={`inner ${
                cell?.column?.id === "selectable-rows" ? "px-3 py-1" : ""
              }`}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </Fragment>
        ) : (
          <Fragment key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Fragment>
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns?.length, selectable, ...deps]);

  const selectableCells = selectable
    ? rows.map((row: any) => {
        return row.getVisibleCells().flatMap((cell: any) => {
          if (cell?.column?.id !== "selectable-rows") return [];

          return [
            <Fragment key={cell.id}>
              <div
                className={`inner ${
                  cell?.column?.id === "selectable-rows" ? "px-3 py-1" : ""
                }`}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            </Fragment>,
          ];
        });
      })
    : null;

  if (error) return null;

  return (
    <tbody className="gc-table-tbody">
      {table.getRowModel().rows.map((row: any, i: number) => {
        const selected = row.getIsSelected();

        if (loading) {
          return (
            <tr
              key={row.id}
              className="gc-table-td selectable-rows gc-table-row"
            >
              {row.getVisibleCells().map((cell: any, j: number) => (
                <td
                  className={`gc-table-td px-3 ${selected ? selectedCSS : ""} ${
                    ["selectable-rows", "_RecordName"].includes(
                      cell?.column?.id
                    )
                      ? `gc-table-fixed ${cell?.column?.id}`
                      : "pl-2 py-1"
                  }`}
                  key={j}
                >
                  <SkeletonLoader height={25} />
                </td>
              ))}
            </tr>
          );
        }

        return (
          <tr key={row.id} className="gc-table-row">
            {row.getVisibleCells().map((cell: any, j: number) => {
              const cellID = cell?.column?.id;

              return (
                <SortableContext
                  items={items}
                  strategy={horizontalListSortingStrategy}
                  key={cell.id}
                >
                  <TableCell
                    selected={selected}
                    cellID={cellID}
                    isMobile={isMobile}
                    selectable={selectable}
                    selectableCells={selectableCells}
                    cells={cells}
                    i={i}
                    j={j}
                  />
                </SortableContext>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

export default AppTableBody;

const TableCell = forwardRef<HTMLTableCellElement, any>(
  (
    {
      selected,
      cellID,
      isMobile,
      style,
      selectable,
      selectableCells,
      cells,
      i,
      j,
      ...props
    },
    ref
  ) => {
    return (
      <td
        className={`gc-table-td ${selected ? selectedCSS : ""} ${
          ["selectable-rows", isMobile ? "" : "_RecordName"].includes(cellID)
            ? `gc-table-fixed ${cellID}`
            : "pl-2 py-1"
        }`}
        ref={ref}
        style={{ opacity: "1 !important", ...style }}
        {...props}
      >
        {selectable && j < 1 ? selectableCells[i][j] : cells[i][j]}
      </td>
    );
  }
);
